import { useContext } from 'react';
import { StructureCopyContext } from '../context';
import {
    DestinationStructure,
    StructureCopyAction,
    StructureParams,
    SourceType,
    StoreyReferenceItem,
} from '../types';
import { Structure } from 'api/index';

export const useStructureManagement = () => {
    const context = useContext(StructureCopyContext);

    if (context === undefined) {
        throw new Error('useStructureManagement must be used within a StructureCopyProvider');
    }

    const { state, dispatch } = context;

    const enterCopyMode = () => {
        dispatch({ type: StructureCopyAction.EnterCopyMode });
    };

    const exitCopyMode = () => {
        dispatch({ type: StructureCopyAction.ExitCopyMode });
    };

    const toggleDestinations = (destination: DestinationStructure) => {
        dispatch({ type: StructureCopyAction.ToggleDestinations, payload: destination });
    };

    const setDestinationSelection = (value: boolean) => {
        dispatch({ type: StructureCopyAction.SetDestinationSelection, payload: value });
    };

    const clearDestinations = () => {
        dispatch({ type: StructureCopyAction.ClearDestinations });
    };

    const toggleOriginStructure = (sourceStructure: StructureParams) => {
        dispatch({ type: StructureCopyAction.ToggleOriginStructure, payload: sourceStructure });
    };

    const enterReferenceCopyMode = () => {
        dispatch({ type: StructureCopyAction.EnterReferenceCopyMode });
    };

    const setNameToDestination = (destination: DestinationStructure, name: string) => {
        dispatch({
            type: StructureCopyAction.SetNameToDestination,
            payload: { destination, name },
        });
    };

    const setReferenceFlag = (value: boolean) => {
        dispatch({ type: StructureCopyAction.SetReferenceFlag, payload: value });
    };

    const setReferenceStructureId = (value: string | null) => {
        dispatch({ type: StructureCopyAction.SetReferenceStructureId, payload: value });
    };

    const setOriginStructures = (value: Structure[]) => {
        dispatch({ type: StructureCopyAction.SetOriginStructures, payload: value });
    };

    const clearOriginStructures = () => {
        dispatch({ type: StructureCopyAction.ClearOriginStructures });
    };

    const clearOriginStructure = () => {
        dispatch({ type: StructureCopyAction.ClearOriginStructure });
    };

    const setSourceType = (value: SourceType) => {
        dispatch({ type: StructureCopyAction.SetSourceType, payload: value });
    };

    const setStoreyReferencesData = (data: StoreyReferenceItem[]) => {
        dispatch({ type: StructureCopyAction.SetStoreyReferencesData, payload: data });
    };

    const addDestination = (destination: DestinationStructure) => {
        dispatch({ type: StructureCopyAction.AddDestination, payload: destination });
    };

    return {
        state,
        enterCopyMode,
        exitCopyMode,
        toggleDestinations,
        clearDestinations,
        toggleOriginStructure,
        setDestinationSelection,
        enterReferenceCopyMode,
        setNameToDestination,
        setReferenceFlag,
        setReferenceStructureId,
        setOriginStructures,
        clearOriginStructures,
        clearOriginStructure,
        setSourceType,
        setStoreyReferencesData,
        addDestination,
    };
};
